/**
 * The console's line to one Fansly account: the CRM's Fansly pass-through
 * (/crm/api/accounts/:id/fansly-shell/crm/...), with the console's Supabase
 * JWT. The CRM checks access, the route allowlist (crm/.../fansly-crm/lib/
 * contract.js) and permissions, then forwards to the Fansly gateway
 * (fansly-server), which answers Fansly's own `{success, response}` body.
 */
import { unwrap } from './data'

export type QueryValue = string | number | boolean | null | undefined

export interface PostOptions {
  /**
   * The operator saw the prohibited-wording warning and sends anyway
   * (the CRM answered 422 `prohibited_terms_confirmation_required`).
   */
  confirmProhibited?: boolean
  /** Reuse a key to retry the same action; a new one is made per call otherwise. */
  idempotencyKey?: string
}

export interface FanslyApi {
  readonly accountId: string
  /** GET a /crm/... route; resolves with the unwrapped `response`. Identical in-flight reads are shared. */
  get: <T = unknown>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) => Promise<T>
  /** POST a /crm/... route; resolves with the unwrapped `response`. */
  post: <T = unknown>(path: string, body?: Record<string, unknown>, opts?: PostOptions) => Promise<T>
  /** POST and get the whole body back (the send-* routes add `media` beside `response`). */
  postFull: <T = Record<string, unknown>>(path: string, body?: Record<string, unknown>, opts?: PostOptions) => Promise<T>
  /** Multipart POST (media upload). */
  upload: <T = unknown>(path: string, form: FormData) => Promise<T>
  /** PUT one upload part to Fansly's storage through the CRM relay (egress via the account proxy); the part's ETag. */
  relayPut: (url: string, part: Blob) => Promise<string>
  /** Translate through the CRM (rate limited per user). */
  translate: (text: string, target: string, source?: string) => Promise<{ translated: string; source: string; target: string; same: boolean }>
}

/** A failure the operator can read: `detail` is already operator-safe (the CRM normalises it). */
export class FanslyError extends Error {
  readonly status: number
  readonly code: string
  /** The flagged words, for code `prohibited_terms_confirmation_required`. */
  readonly terms: string[]
  constructor(message: string, status: number, code = '', terms: string[] = []) {
    super(message)
    this.name = 'FanslyError'
    this.status = status
    this.code = code
    this.terms = terms
  }
  get needsProhibitedConfirmation(): boolean {
    return this.code === 'prohibited_terms_confirmation_required'
  }
  /** The account's Fansly session is gone: the operator must sign in to Fansly again. */
  get disconnected(): boolean {
    return this.code === 'fansly_connection' || this.status === 424
  }
}

export interface Transport {
  getToken: () => string | null
  refreshToken: () => Promise<string | null>
  fetchImpl?: typeof fetch
}

const CRM = '/crm'

/** Operator-facing words for statuses that come back without a CRM `detail`. */
const FALLBACK: Record<number, string> = {
  401: 'Your session has expired. Sign in again.',
  403: "You don't have permission to do this.",
  404: "This isn't available for the account.",
  413: 'The file is too large.',
  415: 'Fansly needs a photo, video or audio file.',
  424: 'This Fansly account is not connected right now.',
  429: 'Fansly is limiting requests. Try again in a moment.',
  501: 'This Fansly feature is not available yet.',
  502: 'Fansly did not respond. Try again.',
  503: 'Fansly did not respond. Try again.',
}

export function queryString(query: Record<string, QueryValue> = {}): string {
  const parts: string[] = []
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
  }
  return parts.length ? `?${parts.join('&')}` : ''
}

function newKey(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c?.randomUUID) return c.randomUUID()
  return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`
}

type Body = Record<string, unknown> | null

function asBody(v: unknown): Body {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

/** The error an answer carries, or null: CRM `{ok:false,…}`, gateway `{error}`, Fansly 200 `{success:false}`. */
export function errorOf(status: number, body: unknown): FanslyError | null {
  const b = asBody(body)
  const ok = status >= 200 && status < 300
  if (ok && b?.success !== false && b?.ok !== false) return null
  const pick = (...vals: unknown[]) => vals.find((v) => typeof v === 'string' && v.trim()) as string | undefined
  const nested = asBody(b?.error)
  const detail = pick(b?.detail, nested?.message, b?.message, typeof b?.error === 'string' && ok ? b.error : undefined)
  const code = pick(b?.code, typeof b?.error === 'string' ? b.error : undefined) ?? ''
  const terms = Array.isArray(b?.terms) ? (b!.terms as unknown[]).map(String) : []
  const s = ok ? 502 : status
  return new FanslyError(detail ?? FALLBACK[s] ?? 'Fansly could not process this request.', s, code, terms)
}

export function createFanslyApi(accountId: string, transport: Transport): FanslyApi {
  const fetchImpl = transport.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a))
  const base = `${CRM}/api/accounts/${encodeURIComponent(accountId)}/fansly-shell`

  /** One request; a 401 refreshes the console session once and retries. */
  const send = async (url: string, init: RequestInit): Promise<Response> => {
    const attempt = (token: string | null) =>
      fetchImpl(url, {
        ...init,
        cache: 'no-store',
        headers: { Accept: 'application/json', 'X-Account-Id': accountId, ...(init.headers as Record<string, string>), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
    const res = await attempt(transport.getToken())
    if (res.status !== 401) return res
    const fresh = await transport.refreshToken()
    return fresh ? attempt(fresh) : res
  }

  const parse = async (res: Response): Promise<unknown> => {
    const text = await res.text().catch(() => '')
    if (!text) return null
    try {
      return JSON.parse(text)
    } catch {
      return { detail: res.ok ? undefined : FALLBACK[res.status] }
    }
  }

  const reads = new Map<string, Promise<unknown>>()

  const read = async (path: string, query: Record<string, QueryValue> | undefined, signal?: AbortSignal) => {
    const res = await send(`${base}${path}${queryString(query)}`, { method: 'GET', signal })
    const body = await parse(res)
    const err = errorOf(res.status, body)
    if (err) throw err
    return body
  }

  const write = async (path: string, body: Record<string, unknown> | undefined, opts: PostOptions = {}) => {
    const payload = { ...(body ?? {}) }
    if (opts.confirmProhibited) payload._crm_prohibited_confirmed = true
    const res = await send(`${base}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Idempotency-Key': opts.idempotencyKey ?? newKey() },
      body: JSON.stringify(payload),
    })
    const parsed = await parse(res)
    const err = errorOf(res.status, parsed)
    if (err) throw err
    return parsed
  }

  return {
    accountId,
    get<T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) {
      // A cancellable read is the caller's own; shared reads have no signal.
      if (signal) return read(path, query, signal).then((b) => unwrap(b) as T)
      const key = `${path}${queryString(query)}`
      let out = reads.get(key)
      if (!out) {
        out = read(path, query).finally(() => reads.delete(key))
        reads.set(key, out)
      }
      return out.then((b) => unwrap(b) as T)
    },
    async post<T>(path: string, body?: Record<string, unknown>, opts?: PostOptions) {
      return unwrap(await write(path, body, opts)) as T
    },
    async postFull<T>(path: string, body?: Record<string, unknown>, opts?: PostOptions) {
      return ((await write(path, body, opts)) ?? {}) as T
    },
    async upload<T>(path: string, form: FormData) {
      const res = await send(`${base}${path}`, { method: 'POST', body: form, headers: { 'X-Idempotency-Key': newKey() } })
      const body = await parse(res)
      const err = errorOf(res.status, body)
      if (err) throw err
      return unwrap(body) as T
    },
    async relayPut(url: string, part: Blob) {
      const form = new FormData()
      form.append('url', url)
      form.append('method', 'PUT')
      // Fansly's own client sends the part with no Content-Type.
      form.append('content_type', '')
      form.append('headers', JSON.stringify({ 'ngsw-bypass': 'true' }))
      form.append('account_id', accountId)
      form.append('file', part, 'part')
      const res = await send(`${CRM}/v1/upload/cdn-relay?account_id=${encodeURIComponent(accountId)}`, { method: 'POST', body: form })
      const body = asBody(await parse(res))
      if (!res.ok || body?.ok === false) throw errorOf(res.status >= 400 ? res.status : 502, body) ?? new FanslyError('Upload failed.', res.status)
      // The CRM wraps the PUT's answer: {ok, data:{status, body:{etag}}}. Keep the raw (quoted) ETag.
      const data = asBody(body?.response) ?? asBody(body?.data) ?? body
      const inner = asBody(data?.body)
      const etag = [inner?.etag, inner?.ETag, inner?.eTag, data?.etag, data?.ETag, data?.eTag].find((v) => typeof v === 'string' && v.trim())
      if (typeof etag !== 'string') throw new FanslyError('Fansly did not confirm the upload part.', 502)
      return etag
    },
    async translate(text: string, target: string, source?: string) {
      const res = await send(`${CRM}/api/accounts/${encodeURIComponent(accountId)}/fansly-translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, target, ...(source ? { source } : {}) }),
      })
      const body = await parse(res)
      const err = errorOf(res.status, body)
      if (err) throw err
      const b = asBody(body) ?? {}
      return { translated: String(b.translated ?? ''), source: String(b.source ?? ''), target: String(b.target ?? target), same: b.same === true }
    },
  }
}
